import { NavLink, Navigate, Route, Routes } from 'react-router-dom';

import { useAutenticazione } from './auth/contesto.tsx';
import { puoAmministrare } from './auth/ruoli.ts';
import { Accesso } from './pagine/Accesso.tsx';
import { Attivita } from './pagine/Attivita.tsx';
import { Cruscotto } from './pagine/Cruscotto.tsx';
import { Profilo } from './pagine/Profilo.tsx';
import { ElencoScadenze } from './pagine/ElencoScadenze.tsx';
import { CambioPassword } from './pagine/CambioPassword.tsx';
import { Aggiornamenti } from './pagine/impostazioni/Aggiornamenti.tsx';
import { Allegati as ImpostazioniAllegatiPagina } from './pagine/impostazioni/Allegati.tsx';
import { AspettoAvvisi } from './pagine/impostazioni/AspettoAvvisi.tsx';
import { AvvisiAutomatici } from './pagine/impostazioni/AvvisiAutomatici.tsx';
import { Impostazioni } from './pagine/impostazioni/Impostazioni.tsx';
import { ServerDiPosta } from './pagine/impostazioni/ServerDiPosta.tsx';
import { Soggetti } from './pagine/Soggetti.tsx';
import { Categorie } from './pagine/Categorie.tsx';
import { Tipi } from './pagine/Tipi.tsx';
import { Utenti } from './pagine/Utenti.tsx';
import { Marchio } from './componenti/Marchio.tsx';
import { MenuUtente } from './componenti/MenuUtente.tsx';
import { dataEstesa, oggi } from './componenti/formato.ts';
import {
  ProvvederAvvisoFermo,
  StrisciaFermo,
  useAltezzaTesta,
} from './componenti/AvvisoFermo.tsx';

export function App() {
  const { utente, inVerifica } = useAutenticazione();

  // Finché non si sa se la sessione è valida non si mostra niente: far
  // lampeggiare la schermata di accesso a chi è già dentro dà l'impressione
  // che l'applicazione l'abbia buttato fuori.
  if (inVerifica) return <div className="stato-caricamento">Caricamento…</div>;

  if (utente === null) return <Accesso />;

  // Password provvisoria: non c'è nient'altro da mostrare, perché non c'è
  // nient'altro che si possa fare. Il backend rifiuterebbe comunque ogni
  // chiamata — qui si spiega il perché invece di far collezionare errori.
  if (utente.deveCambiarePassword) return <CambioPassword />;

  return (
    <ProvvederAvvisoFermo>
      {/* Striscia e intestazione stanno in un contenitore comune
          (`.pagina-testa`), ed è quello a restare ancorato in cima mentre la
          pagina scorre — stesso schema di `.dialogo-testa` dentro `Dialogo`.
          Sopra l'intestazione **nel flusso**, non sovrapposta: spinge giù la
          pagina invece di coprire la navigazione. Chi legge un avviso di
          fermo deve poter continuare a lavorare per i minuti che gli
          restano — è tutto il punto del preavviso. */}
      <PaginaTesta />
      <main className="contenuto">
        <Routes>
          <Route path="/" element={<Cruscotto />} />
          <Route path="/scadenze" element={<ElencoScadenze />} />
          {/* Creazione e modifica non hanno un indirizzo proprio: sono
              dialoghi sopra l'elenco, così i filtri impostati non si perdono. */}
          <Route path="/soggetti" element={<Soggetti />} />
          <Route path="/tipi" element={<Tipi />} />
          <Route path="/categorie" element={<Categorie />} />

          {/* Le due pagine del menu del bollino. Non stanno in cima fra le
              sezioni e non stanno nelle impostazioni: riguardano la propria
              persona, non il lavoro di tutti né la configurazione.

              «Attività» è una sola rotta anche se ha due viste: quale delle
              due si stia guardando è un parametro dell'indirizzo, così un
              amministratore può mandare a un collega il collegamento a una
              ricerca già filtrata. La guardia vera resta sul backend, che ha
              due rotte separate. */}
          <Route path="/profilo" element={<Profilo />} />
          <Route path="/attivita" element={<Attivita />} />

          {/* La rotta esiste per tutti: la pagina spiega da sé che è riservata
              agli amministratori, invece di far comparire un errore 403.

              Le sezioni sono rotte annidate e non schede interne alla pagina:
              l'indirizzo di una sezione si può condividere e mettere nei
              segnalibri, e «indietro» torna alla sezione di prima. */}
          <Route path="/impostazioni" element={<Impostazioni />}>
            <Route index element={<Navigate to="utenti" replace />} />
            <Route path="utenti" element={<Utenti />} />
            <Route path="posta" element={<ServerDiPosta />} />
            <Route path="avvisi" element={<AvvisiAutomatici />} />
            <Route path="aspetto" element={<AspettoAvvisi />} />
            <Route path="allegati" element={<ImpostazioniAllegatiPagina />} />
            <Route path="aggiornamenti" element={<Aggiornamenti />} />
          </Route>

          {/* «Utenti» era una voce del menu in cima e sta nei segnalibri di
              chi la usava ogni giorno: l'indirizzo vecchio continua a
              funzionare invece di scaricare a fondo pagina un cruscotto che
              non era quello che si cercava. */}
          <Route path="/utenti" element={<Navigate to="/impostazioni/utenti" replace />} />
          {/* Un indirizzo sconosciuto riporta al cruscotto invece di mostrare
              una pagina vuota che sembra un guasto. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </ProvvederAvvisoFermo>
  );
}

/**
 * Striscia e intestazione, e la misura della propria altezza.
 *
 * ⚠️ È un componente a sé e non un `<div>` dentro `App` per una ragione
 * meccanica: `App` esce presto tre volte (verifica in corso, nessuna sessione,
 * password da cambiare), e un hook chiamato dopo quelle uscite cambierebbe
 * l'ordine degli hook fra un disegno e l'altro. Chiamarlo *prima*, invece, non
 * funzionerebbe: l'effetto girerebbe una volta sola, mentre a schermo c'è
 * ancora l'accesso e il contenitore non esiste — e non tornerebbe più a
 * misurarlo dopo l'ingresso. Un componente proprio nasce quando nasce il
 * contenitore, che è esattamente quando c'è qualcosa da misurare.
 */
function PaginaTesta() {
  const testa = useAltezzaTesta();

  return (
    <div className="pagina-testa" ref={testa}>
      <StrisciaFermo />
      <Intestazione />
    </div>
  );
}

function Intestazione() {
  const { utente } = useAutenticazione();
  if (utente === null) return null;

  return (
    <header className="intestazione">
      <div className="marchio">
        <Marchio />
        <span className="nome-marchio">FMSchedule</span>
      </div>

      <nav className="navigazione" aria-label="Sezioni">
        <NavLink to="/" end>Cruscotto</NavLink>
        {/*
          Il cruscotto sta da una parte e il resto dall'altra, ed è una
          distinzione vera: il cruscotto è dove si *guarda* come va, le altre
          tre sono dove si *lavora*. Il separatore ha `aria-hidden` perché è
          una linea, non un contenuto: a un lettore di schermo direbbe soltanto
          che c'è qualcosa fra due voci, senza dire cosa.
        */}
        <span className="separatore-menu" aria-hidden="true" />
        <NavLink to="/scadenze">Elenco scadenze</NavLink>
        <NavLink to="/soggetti">Soggetti</NavLink>
        <NavLink to="/tipi">Tipi</NavLink>
        <NavLink to="/categorie">Categorie</NavLink>
        {/* «Utenti» non sta più qui: è una sezione delle impostazioni. In cima
            restano le quattro sezioni che si aprono davvero ogni mattina. */}
      </nav>

      <div className="intestazione-destra">
        {/* L'ingranaggio sta qui e non nel menu del bollino perché le
            impostazioni valgono per **tutta l'applicazione e per tutti**,
            mentre lì dentro c'è ciò che riguarda la propria persona. Messe
            accanto al proprio nome sembravano preferenze personali, che non
            sono: da lì si decide chi entra e da quale casella partono gli
            avvisi di tutto l'ufficio. */}
        {puoAmministrare(utente.ruolo) && (
          <NavLink to="/impostazioni" className="ingranaggio" title="Impostazioni">
            <IconaIngranaggio />
            <span className="solo-lettori-schermo">Impostazioni</span>
          </NavLink>
        )}
        <span>{dataEstesa(oggi())}</span>
        <MenuUtente utente={utente} />
      </div>
    </header>
  );
}

/**
 * L'ingranaggio delle impostazioni, nell'intestazione.
 *
 * ⚠️ **Corona dentata, non raggi.** La prima versione era un cerchio con otto
 * trattini staccati intorno: quello è il glifo della **luminosità**, e Enrico
 * l'ha riconosciuto al primo sguardo. La differenza non è un dettaglio da
 * disegnatori — i denti toccano il corpo e ne fanno parte, i raggi ne stanno
 * fuori, ed è esattamente ciò che l'occhio usa per distinguere i due simboli
 * senza leggere niente.
 *
 * Il tracciato è un unico contorno chiuso: dieci denti trapezoidali generati a
 * mano su una circonferenza di raggio 9, più il mozzo come cerchio separato.
 */
function IconaIngranaggio() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={CORONA_DENTATA} />
      <circle cx="12" cy="12" r="3.1" />
    </svg>
  );
}

/**
 * Il contorno della corona, calcolato una volta sola.
 *
 * Dieci denti: per ognuno si va al raggio esterno, si percorre la testa del
 * dente, si torna al raggio interno e si percorre il vano. Scritto come
 * costante e non ricalcolato a ogni render perché è una figura ferma.
 */
const CORONA_DENTATA = (() => {
  const denti = 10;
  const [interno, esterno] = [7.1, 10.2];
  const passo = (Math.PI * 2) / denti;
  // Quanto è larga la testa del dente rispetto al vano: sotto 0.5 il dente si
  // assottiglia e a 18px sparisce, sopra 0.5 la corona sembra un poligono.
  const testa = passo * 0.28;
  const punti: string[] = [];

  for (let d = 0; d < denti; d += 1) {
    const centro = d * passo;
    for (const [angolo, raggio] of [
      [centro - testa, esterno],
      [centro + testa, esterno],
      [centro + testa + (passo / 2 - testa), interno],
      [centro + passo - testa - (passo / 2 - testa), interno],
    ] as [number, number][]) {
      const x = (12 + Math.cos(angolo) * raggio).toFixed(2);
      const y = (12 + Math.sin(angolo) * raggio).toFixed(2);
      punti.push(`${punti.length === 0 ? 'M' : 'L'}${x} ${y}`);
    }
  }

  return `${punti.join('')}Z`;
})();
